import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  Tooltip,
  Divider,
  Stack,
  Fade,
  Zoom,
  alpha,
  useTheme
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  CloudUpload,
  Code,
  Folder,
  RocketLaunch,
  GitHub,
  Terminal,
  CheckCircle,
  Schedule
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:5000');

// Styled Glass Card Component
const GlassCard = ({ children, sx = {}, gradient = false, ...props }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        background: gradient
          ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
          : alpha(theme.palette.background.paper, 0.8),
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 4,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
        },
        ...sx
      }}
      {...props}
    >
      {children}
    </Paper>
  );
};

// Gradient Button Component
const GradientButton = ({ children, variant = 'primary', sx = {}, ...props }) => {
  const gradients = {
    primary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    success: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
    danger: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)',
  };

  return (
    <Button
      sx={{
        background: gradients[variant],
        color: 'white',
        textTransform: 'none',
        fontWeight: 600,
        px: 3,
        py: 1.5,
        borderRadius: 3,
        boxShadow: `0 4px 15px ${alpha('#667eea', 0.4)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          background: gradients[variant],
          transform: 'translateY(-2px)',
          boxShadow: `0 6px 20px ${alpha('#667eea', 0.6)}`,
        },
        '&:disabled': {
          background: 'linear-gradient(135deg, #bdc3c7 0%, #95a5a6 100%)',
          boxShadow: 'none',
        },
        ...sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export default function DeploymentConfigs({ onMessage }) {
  const theme = useTheme();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [deploying, setDeploying] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'web',
    repoPath: '',
    targetPath: '',
    command: '',
    branch: 'main'
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/deployment-configs`, {
        withCredentials: true
      });
      setConfigs(response.data.configs || []);
    } catch (error) {
      console.error('Error fetching deployment configs:', error);
      onMessage({ type: 'error', text: 'Failed to load deployment configurations' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        name: config.name,
        type: config.type,
        repoPath: config.repo_path,
        targetPath: config.target_path || '',
        command: config.command || '',
        branch: config.branch || 'main'
      });
    } else {
      setEditingConfig(null);
      setFormData({
        name: '',
        type: 'web',
        repoPath: '',
        targetPath: '',
        command: '',
        branch: 'main'
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
    setFormData({
      name: '',
      type: 'web',
      repoPath: '',
      targetPath: '',
      command: '',
      branch: 'main'
    });
  };

  const handleSave = async () => {
    try {
      if (editingConfig) {
        await axios.put(`${API_BASE_URL}/api/deployment-configs/${editingConfig.id}`, formData, {
          withCredentials: true
        });
        onMessage({ type: 'success', text: 'Deployment configuration updated successfully' });
      } else {
        await axios.post(`${API_BASE_URL}/api/deployment-configs`, formData, {
          withCredentials: true
        });
        onMessage({ type: 'success', text: 'Deployment configuration created successfully' });
      }
      fetchConfigs();
      handleCloseDialog();
    } catch (error) {
      onMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to save deployment configuration'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this deployment configuration?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/api/deployment-configs/${id}`, {
        withCredentials: true
      });
      onMessage({ type: 'success', text: 'Deployment configuration deleted successfully' });
      fetchConfigs();
    } catch (error) {
      onMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to delete deployment configuration'
      });
    }
  };

  const handleDeploy = async (configName) => {
    setDeploying(configName);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/deploy/${configName}`, {}, {
        withCredentials: true
      });
      onMessage({ type: 'success', text: response.data.message });
    } catch (error) {
      onMessage({
        type: 'error',
        text: error.response?.data?.error || 'Deployment failed'
      });
    } finally {
      setDeploying(null);
    }
  };

  const isFormValid = () => {
    if (!formData.name || !formData.repoPath) return false;
    if (formData.type === 'web' && !formData.targetPath) return false;
    if (formData.type === 'app' && !formData.command) return false;
    return true;
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 8,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress
          size={48}
          sx={{
            color: 'transparent',
            '& .MuiCircularProgress-circle': {
              stroke: 'url(#gradient)'
            }
          }}
        />
        <svg width="0" height="0">
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#667eea" />
              <stop offset="100%" stopColor="#764ba2" />
            </linearGradient>
          </defs>
        </svg>
        <Typography color="text.secondary" fontWeight={500}>
          Loading configurations...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4
      }}>
        <Box>
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 0.5
            }}
          >
            Deployment Configurations
          </Typography>
          <Typography variant="body1" color="text.secondary" fontWeight={500}>
            Manage and deploy your applications with one click
          </Typography>
        </Box>
        <GradientButton
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          New Deployment
        </GradientButton>
      </Box>

      {configs.length === 0 ? (
        <Fade in timeout={500}>
          <Paper
            sx={{
              p: 8,
              textAlign: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 30% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
              }
            }}
          >
            <RocketLaunch sx={{ fontSize: 80, mb: 3, opacity: 0.9 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Ready for Liftoff
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}>
              Create your first deployment configuration to get started
            </Typography>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 600,
                px: 4,
                py: 1.5,
                borderRadius: 3,
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.95)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0,0,0,0.2)',
                }
              }}
            >
              Create First Deployment
            </Button>
          </Paper>
        </Fade>
      ) : (
        <Grid container spacing={3}>
          {configs.map((config, index) => (
            <Grid item xs={12} md={6} lg={4} key={config.id}>
              <Zoom in timeout={300 + index * 100}>
                <Box>
                  <GlassCard gradient>
                    {/* Card Header */}
                    <Box sx={{
                      p: 3,
                      pb: 2,
                      background: config.type === 'web'
                        ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.15) 100%)'
                        : 'linear-gradient(135deg, rgba(17, 153, 142, 0.15) 0%, rgba(56, 239, 125, 0.15) 100%)',
                      borderRadius: '16px 16px 0 0'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 2.5,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: config.type === 'web'
                              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              : 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}>
                            {config.type === 'web' ? <Folder sx={{ color: 'white' }} /> : <Terminal sx={{ color: 'white' }} />}
                          </Box>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                              {config.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" fontWeight={500}>
                              {config.type === 'web' ? 'Web Deployment' : 'Application'}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          icon={<CheckCircle sx={{ fontSize: 16 }} />}
                          label="Ready"
                          size="small"
                          sx={{
                            background: alpha(theme.palette.success.main, 0.1),
                            color: theme.palette.success.main,
                            fontWeight: 600,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                          }}
                        />
                      </Box>
                    </Box>

                    {/* Card Content */}
                    <Box sx={{ p: 3, pt: 2 }}>
                      <Stack spacing={2.5}>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <GitHub sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                              Repository
                            </Typography>
                          </Box>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              fontSize: '0.8rem',
                              p: 1.5,
                              background: alpha(theme.palette.action.hover, 0.5),
                              borderRadius: 2,
                              wordBreak: 'break-all',
                              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                            }}
                          >
                            {config.repo_path}
                          </Typography>
                        </Box>

                        {config.type === 'web' ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Folder sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                                Target Path
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                p: 1.5,
                                background: alpha(theme.palette.action.hover, 0.5),
                                borderRadius: 2,
                                wordBreak: 'break-all',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                              }}
                            >
                              {config.target_path}
                            </Typography>
                          </Box>
                        ) : (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Code sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                                Command
                              </Typography>
                            </Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.8rem',
                                p: 1.5,
                                background: alpha(theme.palette.action.hover, 0.5),
                                borderRadius: 2,
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                              }}
                            >
                              {config.command}
                            </Typography>
                          </Box>
                        )}

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Chip
                            icon={<Schedule sx={{ fontSize: 14 }} />}
                            label={`Branch: ${config.branch || 'main'}`}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontWeight: 500,
                              borderRadius: 2
                            }}
                          />
                        </Box>
                      </Stack>
                    </Box>

                    {/* Card Actions */}
                    <Divider sx={{ opacity: 0.5 }} />
                    <Box sx={{
                      p: 2,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      background: alpha(theme.palette.action.hover, 0.3)
                    }}>
                      <GradientButton
                        variant="success"
                        onClick={() => handleDeploy(config.name)}
                        disabled={deploying === config.name}
                        startIcon={deploying === config.name ? (
                          <CircularProgress size={18} sx={{ color: 'white' }} />
                        ) : (
                          <CloudUpload />
                        )}
                        sx={{ py: 1 }}
                      >
                        {deploying === config.name ? 'Deploying...' : 'Deploy Now'}
                      </GradientButton>

                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Edit Configuration" arrow>
                          <IconButton
                            onClick={() => handleOpenDialog(config)}
                            sx={{
                              '&:hover': {
                                background: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main
                              }
                            }}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Configuration" arrow>
                          <IconButton
                            onClick={() => handleDelete(config.id)}
                            sx={{
                              '&:hover': {
                                background: alpha(theme.palette.error.main, 0.1),
                                color: theme.palette.error.main
                              }
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  </GlassCard>
                </Box>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            background: alpha(theme.palette.background.paper, 0.95),
            backdropFilter: 'blur(20px)',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              width: 48,
              height: 48,
              borderRadius: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}>
              {editingConfig ? <Edit sx={{ color: 'white' }} /> : <Add sx={{ color: 'white' }} />}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {editingConfig ? 'Edit Configuration' : 'New Deployment'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {editingConfig ? 'Update your deployment settings' : 'Configure a new deployment target'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
            <TextField
              fullWidth
              label="Configuration Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={!!editingConfig}
              helperText="Unique identifier for this deployment"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <FormControl fullWidth required>
              <InputLabel>Deployment Type</InputLabel>
              <Select
                value={formData.type}
                label="Deployment Type"
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="web">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Folder color="primary" fontSize="small" />
                    <Box>
                      <Typography fontWeight={600}>Web Deployment</Typography>
                      <Typography variant="caption" color="text.secondary">Build and deploy to a folder</Typography>
                    </Box>
                  </Box>
                </MenuItem>
                <MenuItem value="app">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Terminal color="success" fontSize="small" />
                    <Box>
                      <Typography fontWeight={600}>Application</Typography>
                      <Typography variant="caption" color="text.secondary">Run with PM2 process manager</Typography>
                    </Box>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Repository Path or URL"
              value={formData.repoPath}
              onChange={(e) => setFormData({ ...formData, repoPath: e.target.value })}
              required
              placeholder="git@github.com:org/repo.git or /path/to/repo"
              helperText="Git SSH/HTTPS URL or local filesystem path"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <TextField
              fullWidth
              label="Branch"
              value={formData.branch}
              onChange={(e) => setFormData({ ...formData, branch: e.target.value })}
              placeholder="main"
              variant="outlined"
              helperText="Git branch to deploy from"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            {formData.type === 'web' ? (
              <TextField
                fullWidth
                label="Target Path"
                value={formData.targetPath}
                onChange={(e) => setFormData({ ...formData, targetPath: e.target.value })}
                required
                placeholder="/var/www/html/myapp"
                helperText="Destination folder for built files"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            ) : (
              <TextField
                fullWidth
                label="Start Command"
                value={formData.command}
                onChange={(e) => setFormData({ ...formData, command: e.target.value })}
                required
                placeholder="node server.js"
                helperText="Command to start the application (e.g., 'node server.js')"
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDialog}
            sx={{
              textTransform: 'none',
              px: 3,
              borderRadius: 2
            }}
          >
            Cancel
          </Button>
          <GradientButton
            onClick={handleSave}
            disabled={!isFormValid()}
          >
            {editingConfig ? 'Save Changes' : 'Create Deployment'}
          </GradientButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
